/**
 * api/explain.ts —— 唯一的「只说话不动手」出口
 *
 * 设计文档 D8 定的是：**explain 兼作通用对话出口**。
 * 所以它不是「出错时的兜底」，而是与所有操作类 API 平级的**一等公民**：
 *
 *   · 做不到某件事   → explain('目前做不到……')
 *   · 普通聊天       → explain('今天天气确实不错')
 *   · 知识问答       → explain('光合作用是……')
 *   · 需要用户补充   → explain('你指的是哪一位联系人？')
 *
 * 为什么它必须**无副作用**（设计文档反复强调）：
 *   信任原则要求「AI 说的每句『我做了什么』都以真实执行结果为前提」。
 *   explain 是 AI 唯一可以在不知道任何执行结果的情况下说话的出口 ——
 *   因为它根本没要求系统做任何事，所以它说的话不存在「谎报」的可能。
 *   一旦给 explain 加上副作用（比如顺手改个状态），
 *   它就从「纯说话」变成了「边说边做」，信任边界立刻模糊。
 *
 * 实现上刻意做得极其简单：把文字存进返回值，什么都不改。
 */

import type { ObjectDef } from '../registry';

/** explain 的文案会被求值器收进 EvalResult.desc，UI 直接展示 */
export const explainApi = {
  name: 'explain',
  desc:
    '向用户说一句话。这是唯一没有副作用的出口。' +
    '做不到用户的请求时用它说明原因；普通聊天、知识问答也用它回答。',
  // explain 是 IL 的内部机制，不是"一项能力" —— 在面向用户的 help 里隐藏。
  hidden: true,
  methods: [
    {
      name: 'call',
      desc: '把一段文字展示给用户。它不会改变应用的任何状态。',
      params: [
        {
          name: 'text',
          type: 'string' as const,
          desc: '要说给用户听的话',
          required: true,
        },
      ],
      // 文案就是入参本身 —— 原样透传，一个字都不改。
      // 这保证了「AI 想说什么」和「用户看到什么」之间零加工。
      successText: (args: unknown[]) => String(args[0] ?? ''),
      run: (args: unknown[]) => ({ value: String(args[0] ?? '') }),
    },
  ],
} satisfies ObjectDef;

/**
 * 为了让 `explain('文字')` 这种直呼式写法也能用，注册时把 call 设为默认方法。
 *
 * 设计取舍：IL 的语法是 `root.method(args)`，严格来说 explain 也该写成
 * `explain.call('文字')`。但 explain 出现频率极高，而且它是模型最容易
 * 写错的一个（因为它太像普通函数了）。所以支持一种**语法糖**：
 *
 *   explain('文字')  ≡  explain.call('文字')
 *
 * 这个糖在注册层处理（见 register.ts），Lexer/Parser 不需要特殊照顾 ——
 * 解析器会把它当成 `explain` 后面跟了参数，这在标准语法里是非法的，
 * 但我们允许「根对象后直接跟参数列表」这种形式，并自动补上默认方法名。
 */
export const EXPLAIN_DEFAULT_METHOD = 'call';
